import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GlobalInput } from "./GlobalInput";
import Add from "@mui/icons-material/Add";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ContentCopy from "@mui/icons-material/ContentCopy";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import FormatIndentDecrease from "@mui/icons-material/FormatIndentDecrease";
import FormatIndentIncrease from "@mui/icons-material/FormatIndentIncrease";
import Restore from "@mui/icons-material/Restore";
import Save from "@mui/icons-material/Save";
import type {
  ReaderNavigationItem,
  TableOfContents,
} from "@ebook/contracts";
import { api, ApiClientError } from "../lib/api-client";
import { useTranslate } from "../i18n";

type FlatItem = {
  id: string;
  title: string;
  pageNumber: number;
  depth: number;
};

function flattenItems(
  items: ReaderNavigationItem[],
  depth = 0,
): FlatItem[] {
  return items.flatMap((item) => [
    {
      id: crypto.randomUUID(),
      title: item.title,
      pageNumber: item.pageNumber,
      depth,
    },
    ...flattenItems(item.children, depth + 1),
  ]);
}

function treeItems(rows: FlatItem[]): ReaderNavigationItem[] {
  const roots: ReaderNavigationItem[] = [];
  const parents: ReaderNavigationItem[] = [];
  for (const row of rows) {
    const item: ReaderNavigationItem = {
      title: row.title.trim(),
      pageNumber: row.pageNumber,
      children: [],
    };
    const depth = Math.min(row.depth, parents.length);
    if (depth === 0) roots.push(item);
    else parents[depth - 1]!.children.push(item);
    parents[depth] = item;
    parents.length = depth + 1;
  }
  return roots;
}

function subtreeEnd(rows: FlatItem[], index: number): number {
  const depth = rows[index]?.depth ?? 0;
  let end = index + 1;
  while (end < rows.length && (rows[end]?.depth ?? 0) > depth) end += 1;
  return end;
}

export function EbookTableOfContentsPanel({
  ebookId,
  pageCount,
}: {
  ebookId: string;
  pageCount: number;
}) {
  const [contents, setContents] = useState<TableOfContents | null>(null);
  const [rows, setRows] = useState<FlatItem[]>([]);
  const [initialItems, setInitialItems] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const contentsRef = useRef<TableOfContents | null>(null);
  const rowsRef = useRef<FlatItem[]>([]);
  const initialItemsRef = useRef("");
  const saveQueueRef = useRef(Promise.resolve());
  const pendingSaveCountRef = useRef(0);
  const __ = useTranslate();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const next = await api.tableOfContents(ebookId);
      const flattened = flattenItems(next.items);
      contentsRef.current = next;
      rowsRef.current = flattened;
      initialItemsRef.current = JSON.stringify(next.items);
      setContents(next);
      setRows(flattened);
      setInitialItems(initialItemsRef.current);
    } catch (caught) {
      setError(caught instanceof Error
        ? caught.message
        : __("โหลดสารบัญไม่สำเร็จ"));
    } finally {
      setLoading(false);
    }
  }, [__, ebookId]);

  useEffect(() => { void load(); }, [load]);

  const currentItems = useMemo(() => treeItems(rows), [rows]);
  const invalid = rows.some((row) =>
    !row.title.trim()
    || row.pageNumber < 1
    || row.pageNumber > pageCount
  );
  const dirty =
    contents?.source !== "manual"
    || JSON.stringify(currentItems) !== initialItems;

  function setDraftRows(nextRows: FlatItem[]) {
    rowsRef.current = nextRows;
    setRows(nextRows);
  }

  function update(index: number, patch: Partial<FlatItem>) {
    const nextRows = rowsRef.current.map((row, rowIndex) =>
      rowIndex === index ? { ...row, ...patch } : row
    );
    setDraftRows(nextRows);
  }

  function remove(index: number) {
    const current = rowsRef.current;
    const end = subtreeEnd(current, index);
    setDraftRows([...current.slice(0, index), ...current.slice(end)]);
  }

  function duplicate(index: number) {
    const current = rowsRef.current;
    const end = subtreeEnd(current, index);
    const block = current.slice(index, end);
    if (block.length === 0 || current.length + block.length > 500) return;
    const copied = block.map((item) => ({
      ...item,
      id: crypto.randomUUID(),
    }));
    setDraftRows([
      ...current.slice(0, end),
      ...copied,
      ...current.slice(end),
    ]);
  }

  function shiftDepth(index: number, direction: 1 | -1) {
    const current = rowsRef.current;
    const row = current[index];
    if (!row) return;
    const nextDepth = row.depth + direction;
    if (
      nextDepth < 0
      || nextDepth > 5
      || (direction > 0 && (index === 0 || nextDepth > current[index - 1]!.depth + 1))
    ) return;
    const end = subtreeEnd(current, index);
    setDraftRows(current.map((item, rowIndex) =>
      rowIndex >= index && rowIndex < end
        ? { ...item, depth: item.depth + direction }
        : item
    ));
  }

  function move(index: number, direction: -1 | 1) {
    const current = rowsRef.current;
    const row = current[index];
    if (!row) return;
    const end = subtreeEnd(current, index);
    const block = current.slice(index, end);
    if (direction < 0) {
      let previous = index - 1;
      while (previous >= 0 && current[previous]!.depth > row.depth) previous -= 1;
      if (previous < 0 || current[previous]!.depth !== row.depth) return;
      setDraftRows([
        ...current.slice(0, previous),
        ...block,
        ...current.slice(previous, index),
        ...current.slice(end),
      ]);
      return;
    }
    if (end >= current.length || current[end]!.depth !== row.depth) return;
    const nextEnd = subtreeEnd(current, end);
    setDraftRows([
      ...current.slice(0, index),
      ...current.slice(end, nextEnd),
      ...block,
      ...current.slice(nextEnd),
    ]);
  }

  async function save() {
    await saveRows(rowsRef.current, { skipUnchanged: false });
  }

  function rowsInvalid(nextRows: FlatItem[]) {
    return nextRows.some((row) =>
      !row.title.trim()
      || row.pageNumber < 1
      || row.pageNumber > pageCount
    );
  }

  function saveRows(nextRows: FlatItem[], options: { skipUnchanged?: boolean } = {}) {
    if (!contentsRef.current) return Promise.resolve(false);
    if (rowsInvalid(nextRows)) return Promise.resolve(false);
    const items = treeItems(nextRows);
    const serializedItems = JSON.stringify(items);
    if (
      options.skipUnchanged
      && contentsRef.current?.source === "manual"
      && serializedItems === initialItemsRef.current
    ) {
      return Promise.resolve(true);
    }
    pendingSaveCountRef.current += 1;
    setSaving(true);
    setError("");

    const saveTask = saveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        const currentContents = contentsRef.current;
        if (!currentContents) return false;
        try {
          const next = await api.updateTableOfContents(
            ebookId,
            items,
            currentContents.revision,
          );
          const flattened = flattenItems(next.items);
          const returnedItems = JSON.stringify(next.items);
          contentsRef.current = next;
          initialItemsRef.current = returnedItems;
          setContents(next);
          setInitialItems(returnedItems);
          if (JSON.stringify(treeItems(rowsRef.current)) === serializedItems) {
            setDraftRows(flattened);
          }
          return true;
        } catch (caught) {
          if (
            caught instanceof ApiClientError
            && caught.code === "TABLE_OF_CONTENTS_CONFLICT"
          ) {
            setError(__("สารบัญถูกแก้ไขจากหน้าต่างอื่น ระบบจะโหลดข้อมูลล่าสุด"));
            await load();
          } else {
            setError(caught instanceof Error ? caught.message : __("บันทึกสารบัญไม่สำเร็จ"));
          }
          return false;
        } finally {
          pendingSaveCountRef.current -= 1;
          if (pendingSaveCountRef.current === 0) setSaving(false);
        }
      });

    saveQueueRef.current = saveTask.then(() => undefined);
    return saveTask;
  }

  function autoSaveOnBlur() {
    void saveRows(rowsRef.current, { skipUnchanged: true });
  }

  async function reset() {
    if (!contents) return;
    setSaving(true);
    setError("");
    try {
      await api.updateTableOfContents(ebookId, null, contents.revision);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : __("คืนค่าสารบัญไม่สำเร็จ"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Box py={8} display="grid" sx={{ placeItems: "center" }}>
      <CircularProgress />
    </Box>;
  }

  const sourceLabel = contents?.source === "manual"
    ? __("แก้ไขเอง")
    : contents?.source === "pdf"
      ? __("PDF Bookmark")
      : __("แนะนำจาก PDF/OCR");

  return <Stack spacing={2.5}>
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      gap={1.5}
    >
      <Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" fontWeight={700}>{__("จัดการสารบัญ")}</Typography>
          <Chip size="small" label={sourceLabel} color="primary" variant="outlined" />
        </Stack>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {__("ระบบใช้ PDF Bookmark ก่อน และใช้ข้อความ PDF/OCR เพื่อเสนอหัวข้อเมื่อไม่พบ Bookmark")}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button
          startIcon={<Restore />}
          disabled={saving || contents?.source !== "manual"}
          onClick={() => void reset()}
        >
          {__("คืนค่าที่ตรวจพบ")}
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} /> : <Save />}
          disabled={saving || invalid || !dirty}
          onClick={() => void save()}
        >
          {__("บันทึกสารบัญ")}
        </Button>
      </Stack>
    </Stack>

    {contents?.source === "ocr" && (
      <Alert severity="info">
        {__("รายการนี้เป็นข้อเสนอจากข้อความที่ระบบอ่านได้ กรุณาตรวจชื่อหัวข้อและเลขหน้าก่อนบันทึก")}
      </Alert>
    )}
    {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

    <Stack spacing={1}>
      {rows.map((row, index) => (
        <Box
          key={row.id}
          sx={{
            p: 1.25,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "stretch",
              gap: 1,
              minWidth: 0,
              pl: { xs: Math.min(row.depth, 5) * 2, sm: Math.min(row.depth, 5) * 3 },
            }}
          >
            <GlobalInput
              label={__("ชื่อหัวข้อ")}
              value={row.title}
              error={!row.title.trim()}
              onChange={(event) => update(index, { title: event.target.value.slice(0, 500) })}
              onBlur={autoSaveOnBlur}
              sx={{ flex: 1, minWidth: 0 }}
            />
          </Box>
          <GlobalInput
            label={__("หน้า")}
            type="number"
            value={row.pageNumber}
            error={row.pageNumber < 1 || row.pageNumber > pageCount}
            onChange={(event) => update(index, {
              pageNumber: Number.parseInt(event.target.value, 10) || 0,
            })}
            onBlur={autoSaveOnBlur}
            inputProps={{ min: 1, max: pageCount }}
            sx={{ width: { xs: "100%", sm: 100 } }}
          />
          <Stack direction="row" spacing={0.25}>
            <Tooltip title={__("เลื่อนขึ้น")}>
              <span><IconButton size="small" onClick={() => move(index, -1)}>
                <ArrowUpward fontSize="small" />
              </IconButton></span>
            </Tooltip>
            <Tooltip title={__("เลื่อนลง")}>
              <span><IconButton size="small" onClick={() => move(index, 1)}>
                <ArrowDownward fontSize="small" />
              </IconButton></span>
            </Tooltip>
            <Tooltip title={__("ลดระดับ")}>
              <span><IconButton
                size="small"
                disabled={row.depth === 0}
                onClick={() => shiftDepth(index, -1)}
              >
                <FormatIndentDecrease fontSize="small" />
              </IconButton></span>
            </Tooltip>
            <Tooltip title={__("ทำเป็นหัวข้อย่อย")}>
              <span><IconButton
                size="small"
                disabled={index === 0 || row.depth >= 5}
                onClick={() => shiftDepth(index, 1)}
              >
                <FormatIndentIncrease fontSize="small" />
              </IconButton></span>
            </Tooltip>
            <Tooltip title={__("คัดลอกหัวข้อนี้")}>
              <span><IconButton
                size="small"
                disabled={rows.length + (subtreeEnd(rows, index) - index) > 500}
                onClick={() => duplicate(index)}
                aria-label={__("คัดลอกหัวข้อนี้")}
              >
                <ContentCopy fontSize="small" />
              </IconButton></span>
            </Tooltip>
            <Tooltip title={__("ลบหัวข้อนี้และหัวข้อย่อย")}>
              <IconButton size="small" color="error" onClick={() => remove(index)}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      ))}
    </Stack>

    {rows.length === 0 && (
      <Alert severity="warning">
        {__("ยังไม่พบหัวข้อจาก PDF หรือ OCR สามารถเพิ่มสารบัญเองได้")}
      </Alert>
    )}
    <Box>
      <Button
        startIcon={<Add />}
        disabled={rows.length >= 500}
        onClick={() => setDraftRows([
          ...rowsRef.current,
          {
            id: crypto.randomUUID(),
            title: "",
            pageNumber: 1,
            depth: 0,
          },
        ])}
      >
        {__("เพิ่มหัวข้อ")}
      </Button>
    </Box>
  </Stack>;
}
